import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import styles from '../styles/curve.module.css';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PremiumHandler from "../components/PremiumHandler";
import { BACKEND_URL } from "../config";

//remplacer les 2 constantes ci dessous par des valeurs calculées intelligement 
const SCROLL_WIDTH = 6000; // Largeur fictive de la div scrollable (impacte la précision et la sensibilité)
const WINDOW_SIZE = 40000; //proportionnel au nombre minimal de donné affichable (scroll full gauche)

const CurvePage = () => {
  const [curveData, setCurveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const curveColorsRef = useRef({}); // Pour stocker les couleurs des courbes
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  
  const [scrollMode, setScrollMode] = useState("expansion"); // "expansion" ou "window"
  const [xRange, setXRange] = useState({ min: 0, max: WINDOW_SIZE });
  const [chartWidth, setChartWidth] = useState(window.innerWidth - 40);
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [plannedTimePoint, setPlannedTimePoint] = useState("");
  const [processingAction, setProcessingAction] = useState(false);
  const [quotaErrorMessage, setQuotaErrorMessage] = useState("");

  // Récupération du nom de révision et userId depuis l'URL
  const getRevisionName = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("name") || "";
  }, [location.search]);

  const getUserId = useCallback(() => {
    // Ne plus utiliser l'ID depuis l'URL, uniquement celui de la session
    return sessionStorage.getItem("user_id") || "";
  }, []);

  // Mise à jour du titre de l'onglet du navigateur
  useEffect(() => {
    const revisionName = getRevisionName();
    document.title = revisionName || "Memory Curve";
  }, [getRevisionName]);

  // Vérification du statut premium
  const checkPremiumStatus = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return false;
    
    const isPremiumCached = sessionStorage.getItem("is_premium") === "true";
    
    if (isPremiumCached) {
      setIsPremium(true);
      return true;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/user/${userId}`);
      const data = await response.json();
      
      if (data.is_premium) {
        sessionStorage.setItem("is_premium", "true");
        setIsPremium(true);
        return true;
      } else {
        sessionStorage.setItem("is_premium", "false");
        setIsPremium(false);
        return false;
      }
    } catch (error) {
      console.error("Error checking premium status:", error);
      return false;
    }
  }, [getUserId]);
  
  // Vérification de l'état des notifications
  const checkNotificationStatus = useCallback(async () => {
    const revisionName = getRevisionName();
    const userId = getUserId();
    
    if (!revisionName || !userId) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/revision-notifications?user_id=${userId}&revision_name=${encodeURIComponent(revisionName)}`
      );
      const data = await response.json();
      
      if (response.ok) {
        setNotificationsEnabled(data.notifications_enabled);
      }
    } catch (error) {
      console.error("Error fetching notification status:", error);
    }
  }, [getRevisionName, getUserId]);

  // Récupération des données API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Vérification d'authentification
        const userId = getUserId();
        if (!userId) {
          alert("Please log in first.");
          navigate("/login");
          return;
        }
        
        setLoading(true);
        
        // Récupération du nom de révision
        const revisionName = getRevisionName();

        if (!revisionName) {
          throw new Error("Missing parameter: revision name");
        }

        const response = await fetch(
          `${BACKEND_URL}/get-memory-curve?revision=${encodeURIComponent(revisionName)}&user_id=${encodeURIComponent(userId)}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Data fetched:", data);
        setCurveData(data);
        setLoading(false);
        
        // Vérification du statut premium
        await checkPremiumStatus();
        
        // Vérification de l'état des notifications
        await checkNotificationStatus();
      } catch (err) {
        console.error("Error fetching memory curve data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search, checkPremiumStatus, checkNotificationStatus, navigate, getUserId, getRevisionName]);

  // Initialiser les couleurs pour les courbes quand les données sont chargées
  useEffect(() => {
    if (curveData && curveData.curves) {
      curveData.curves.forEach(curve => {
        const curveId = curve.futurecurves 
          ? `future-${curve.revision}` 
          : `revision-${curve.revision}`;
        
        // N'assigne une couleur que si elle n'existe pas déjà
        if (!curveColorsRef.current[curveId]) {
          if (curve.futurecurves) {
            // Les courbes futures sont toujours rouges
            curveColorsRef.current[curveId] = 'red';
          } else {
            // Génère une couleur aléatoire pour les autres courbes
            const r = Math.floor(Math.random() * 255);
            const g = Math.floor(Math.random() * 255);
            const b = Math.floor(Math.floor(0.6 + (0.4 * Math.random())) * 255);
            curveColorsRef.current[curveId] = `rgb(${r}, ${g}, ${b})`;
          }
        }
      });
    }
  }, [curveData]);

  // Met à jour la taille du graphique quand la fenêtre est redimensionnée
  useEffect(() => {
    const handleResize = () => setChartWidth(window.innerWidth - 40);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Bouton pour retourner à la page principale
  const handleBackToMenu = () => {
    navigate("/main");
  };

  // Fonction de scroll pour le mode "expansion" (xMin = 0, xMax augmente)
  const handleScrollExpansion = () => {
    if (!curveData) return;
    
    const el = scrollRef.current;
    const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    const maxDuration = curveData.extremeDuration || WINDOW_SIZE * 2;
    const newMax = WINDOW_SIZE + Math.round(scrollRatio * (maxDuration - WINDOW_SIZE));
    setXRange({ min: 0, max: newMax });
  };

  // Fonction de scroll pour le mode "window" (xMin et xMax glissent)
  const handleScrollWindow = () => {
    if (!curveData) return;
    
    const el = scrollRef.current;
    const scrollRatio = el.scrollLeft / (el.scrollWidth - el.clientWidth);
    const maxDuration = curveData.extremeDuration || WINDOW_SIZE * 2;
    const newMin = Math.round(scrollRatio * (maxDuration - WINDOW_SIZE));
    setXRange({ min: newMin, max: newMin + WINDOW_SIZE });
  };

  // Redirige le scroll selon le mode actif
  const handleScroll = () => {
    if (scrollMode === "expansion") {
      handleScrollExpansion();
    } else {
      handleScrollWindow();
    }
  };

  // Formatage du temps pour l'affichage des labels
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };
  
  // Ajout d'un point de temps instantané
  const handleAddInstantTimePoint = async () => {
    setProcessingAction(true);
    const revisionName = getRevisionName();
    const userId = getUserId();
    
    if (!userId) {
      alert("Please log in first.");
      navigate("/login");
      setProcessingAction(false);
      return;
    }
    
    if (!revisionName) {
      alert("Please select a revision.");
      setProcessingAction(false);
      return;
    }

    const currentTimestamp = Math.floor(Date.now() / 1000); // Timestamp actuel en secondes

    const payload = {
      user_id: userId,
      revision_name: revisionName,
      new_time_point: currentTimestamp,
    };

    try {
      const response = await fetch(`${BACKEND_URL}/add-time-point`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        alert("Current time point added successfully!");
        // Recharger les données de la courbe
        refreshCurveData();
      } else {
        // Vérifier si c'est une erreur de quota (403)
        if (response.status === 403 && data.detail && data.detail.includes("Maximum number of revisions reached")) {
          // Capturer et afficher le message d'erreur
          setQuotaErrorMessage(data.detail);
          setShowPremiumModal(true);
        } else {
          alert(data.detail || "Failed to add time point.");
        }
      }
    } catch (error) {
      console.error("Error adding current time point:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Ajout d'un point de temps planifié
  const handleAddPlannedTimePoint = async () => {
    if (!plannedTimePoint) {
      alert("Please select a valid date and time.");
      return;
    }
    
    setProcessingAction(true);
    const revisionName = getRevisionName();
    const userId = getUserId();
    
    if (!userId) {
      alert("Please log in first.");
      navigate("/login");
      setProcessingAction(false);
      return;
    }
    
    if (!revisionName) {
      alert("Please select a revision.");
      setProcessingAction(false);
      return;
    }

    const timestamp = new Date(plannedTimePoint).getTime();
    const timestampInSeconds = Math.floor(timestamp / 1000);

    const payload = {
      user_id: userId,
      revision_name: revisionName,
      new_time_point: timestampInSeconds,
    };

    try {
      const response = await fetch(`${BACKEND_URL}/add-time-point`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        alert("Time point added successfully!");
        setPlannedTimePoint(""); // Reset the input field
        // Recharger les données de la courbe
        refreshCurveData();
      } else {
        // Vérifier si c'est une erreur de quota (403)
        if (response.status === 403 && data.detail && data.detail.includes("Maximum number of revisions reached")) {
          // Capturer et afficher le message d'erreur
          setQuotaErrorMessage(data.detail);
          setShowPremiumModal(true);
        } else {
          alert(data.detail || "Failed to add time point.");
        }
      }
    } catch (error) {
      console.error("Error adding time point:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Gestion du toggle des notifications
  const handleToggleNotifications = async (e) => {
    const enabled = e.target.checked;
    
    const userId = getUserId();
    if (!userId) {
      e.target.checked = false;
      alert("Please log in first.");
      navigate("/login");
      return;
    }
    
    // Vérifier si l'utilisateur est premium
    const premiumStatus = await checkPremiumStatus();
    
    if (!premiumStatus) {
      e.target.checked = false; // Reset toggle
      setQuotaErrorMessage(""); // Réinitialiser le message d'erreur de quota
      // Définir un message personnalisé pour les notifications premium
      setQuotaErrorMessage("Notifications feature is available for premium users only.");
      setShowPremiumModal(true); // Show premium modal
      return;
    }
    
    const revisionName = getRevisionName();
    
    try {
      const response = await fetch(`${BACKEND_URL}/revision-notifications`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          user_id: parseInt(userId), 
          revision_name: revisionName, 
          enabled: Boolean(enabled)
        }),
      });
      
      if (response.status === 403) {
        setQuotaErrorMessage(""); // Réinitialiser avant d'afficher la modale
        // Définir un message personnalisé pour les notifications premium
        setQuotaErrorMessage("Notifications feature is available for premium users only.");
        setShowPremiumModal(true);
        e.target.checked = false;
      } else if (response.ok) {
        setNotificationsEnabled(enabled);
      } else {
        alert("Failed to update notification settings.");
        e.target.checked = !enabled; // Revert the toggle
      }
    } catch (error) {
      console.error("Error updating notification settings:", error);
      e.target.checked = !enabled; // Revert the toggle
    }
  };
  
  // Suppression de la dernière révision
  const handleDeleteLastRevision = async () => {
    if (!window.confirm("Are you sure you want to delete the last revision?")) {
      return;
    }
    
    setProcessingAction(true);
    const revisionName = getRevisionName();
    const userId = getUserId();
    
    if (!userId) {
      alert("Please log in first.");
      navigate("/login");
      setProcessingAction(false);
      return;
    }
    
    if (!revisionName) {
      alert("Unable to identify the curve.");
      setProcessingAction(false);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/delete-last-revision`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, revision_name: revisionName }),
      });
      
      if (response.ok) {
        alert("Last revision deleted successfully.");
        refreshCurveData();
      } else {
        const data = await response.json();
        alert(data.detail || "Failed to delete the last revision.");
      }
    } catch (error) {
      console.error("Error deleting the last revision:", error);
      alert("An error occurred while deleting the last revision.");
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Suppression complète de la courbe
  const handleDeleteCurve = async () => {
    if (!window.confirm(`Are you sure you want to delete the curve "${getRevisionName()}"?`)) {
      return;
    }
    
    setProcessingAction(true);
    const revisionName = getRevisionName();
    const userId = getUserId();
    
    if (!userId) {
      alert("Please log in first.");
      navigate("/login");
      setProcessingAction(false);
      return;
    }
    
    if (!revisionName) {
      alert("Unable to identify the curve.");
      setProcessingAction(false);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/delete-curve`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, revision_name: revisionName }),
      });
      
      if (response.ok) {
        alert("Curve deleted successfully.");
        navigate("/main"); // Redirect to main page
      } else {
        const data = await response.json();
        alert(data.detail || "Failed to delete the curve.");
      }
    } catch (error) {
      console.error("Error deleting the curve:", error);
      alert("An error occurred while deleting the curve.");
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Rechargement des données de la courbe
  const refreshCurveData = async () => {
    try {
      const userId = getUserId();
      if (!userId) {
        alert("Please log in first.");
        navigate("/login");
        return;
      }

      setLoading(true);
      const revisionName = getRevisionName();
      
      if (!revisionName) {
        throw new Error("Missing parameter: revision name");
      }
      
      const response = await fetch(
        `${BACKEND_URL}/get-memory-curve?revision=${encodeURIComponent(revisionName)}&user_id=${encodeURIComponent(userId)}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setCurveData(data);
      setLoading(false);
    } catch (err) {
      console.error("Error refreshing curve data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Préparation des données pour ECharts
  const getOption = () => {
    if (!curveData) return {};

    // Création des séries pour chaque courbe
    const series = curveData.curves
      .map((curve) => {
        // Formatage des données pour ECharts et filtrage par range X
        const data = curve.data
          .filter(point => point.x >= xRange.min && point.x <= xRange.max)
          .map(point => [point.x, point.y]);
        
        // Identifier unique pour la courbe
        const curveId = curve.futurecurves 
          ? `future-${curve.revision}` 
          : `revision-${curve.revision}`;
        
        // Utiliser la couleur stockée ou en créer une nouvelle si elle n'existe pas
        const color = curveColorsRef.current[curveId] || 'black';

        // Configuration spécifique selon le type de courbe
        if (curve.futurecurves && curveData.displayFutureCurves && !curve.pointilles) {
          return {
            name: curve.name || `Future Revision ${curve.revision}`,
            type: 'scatter',
            data: curve.data.filter(point => point.y !== -1).map(point => [point.x, point.y]),
            itemStyle: { color },
            symbolSize: 8,
          };
        } else if (curve.futurecurves && curveData.displayFutureCurves && curve.pointilles) {
          return {
            name: '', // pas de légende pour les pointillés
            type: 'line',
            data: data,
            lineStyle: { 
              color,
              width: 2,
              type: 'dashed',
              dashOffset: 5
            },
            showSymbol: false,
          };
        } else if (!curve.futurecurves) {
          return {
            name: curve.name || `Revision ${curve.revision}`,
            type: 'line',
            data: data,
            lineStyle: { 
              color,
              width: 3
            },
            showSymbol: false,
          };
        }
        return null;
      })
      .filter(series => series !== null);

    return {
      animation: false,
      grid: { left: 50, right: 20, top: 40, bottom: 40 },
      xAxis: {
        type: "value",
        min: xRange.min,
        max: xRange.max,
        axisLabel: {
          formatter: (val) => formatTime(val)
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: {
          formatter: (val) => `${val}%`,
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: function(params) {
          const timeStr = formatTime(params[0].value[0]);
          const valueStr = params[0].value[1].toFixed(1);
          return `Time: ${timeStr}<br/>Retention: ${valueStr}%`;
        }
      },
      legend: {
        top: 10,
        data: series.filter(s => s.name).map(s => s.name)
      },
      series: series,
    };
  };

  // Affichage du timer de prochaine révision
  const renderNextRevisionTimer = () => {
    if (!curveData || !curveData.timer) return "Calculating next revision time...";
    
    const { days, hours, minutes } = curveData.timer;
    if (minutes > 0) {
      return `Next revision in ${days} days, ${hours} hours, ${minutes} minutes`;
    } else {
      return "You should revise now!";
    }
  };

  if (loading) return <div className={styles.loading}>Loading data...</div>;
  if (error) return <div className={styles.error}>Error: {error}</div>;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        {/* Bouton de retour au menu */}
        <div className={styles.navContainer}>
          <button 
            className={styles.backButton} 
            onClick={handleBackToMenu}
          >
            ← Back to menu
          </button>
        </div>
        
        {/* Section Graphique */}
        <div className={styles.graphSection}>
          <h1 className={styles.title}>{getRevisionName()}</h1>
          
          <div className={styles.infoBar}>
            <div className={styles.rangeInfo}>
              Range: {formatTime(xRange.min)} → {formatTime(xRange.max)}
            </div>
            <div className={styles.timerInfo}>
              {renderNextRevisionTimer()}
            </div>
          </div>

          <div className={styles.graphContainer}>
            {/* Graphique principal */}
            <ReactECharts
              option={getOption()}
              style={{ width: chartWidth, height: 400 }}
              notMerge={true}
              lazyUpdate={true}
            />

            {/* Couche scrollable superposée */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className={styles.scrollOverlay}
            >
              {/* Élément large pour permettre le scroll */}
              <div style={{ width: `${SCROLL_WIDTH}px`, height: "100%" }} />
            </div>
          </div>

          <p className={styles.scrollHelp}>
            Scroll horizontally on the graph to 
            {scrollMode === "expansion" 
              ? " expand the visible data range" 
              : " navigate through the sliding window"}.
          </p>
        </div>
        
        {/* Section Configuration */}
        <div className={styles.configSection}>
          <h2>Configuration</h2>
          
          <div className={styles.configLayout}>
            {/* Add Time Point Section */}
            <div className={styles.configGroup}>
              <h3>Add Revision Time</h3>
              
              <button 
                className={styles.configButton} 
                onClick={handleAddInstantTimePoint}
                disabled={processingAction}
              >
                {processingAction ? "Processing..." : "Add Instant Revision"}
              </button>
              
              <p className={styles.sectionDivider}>Or plan a future revision:</p>
              
              <div className={styles.inputGroup}>
                <input 
                  type="datetime-local" 
                  id="plannedTimePoint"
                  className={styles.dateTimeInput}
                  value={plannedTimePoint}
                  onChange={(e) => setPlannedTimePoint(e.target.value)}
                />
                <button 
                  className={styles.configButton} 
                  onClick={handleAddPlannedTimePoint}
                  disabled={processingAction || !plannedTimePoint}
                >
                  Add
                </button>
              </div>
            </div>
            
            {/* Notification Section */}
            <div className={styles.configGroup}>
              <h3>Notifications</h3>
              <div className={styles.toggleContainer}>
                <label className={styles.toggleSwitch}>
                  <input 
                    type="checkbox" 
                    checked={notificationsEnabled}
                    onChange={handleToggleNotifications}
                  />
                  <span className={styles.toggleSlider}></span>
                </label>
                <span className={styles.toggleLabel}>
                  {notificationsEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
            
            {/* Scroll Type Section */}
            <div className={styles.configGroup}>
              <h3>Scroll Type</h3>
              <select 
                className={styles.selectInput}
                value={scrollMode}
                onChange={(e) => setScrollMode(e.target.value)}
              >
                <option value="expansion">Expansion</option>
                <option value="window">Sliding Window</option>
              </select>
            </div>
            
            {/* Delete Section */}
            <div className={styles.configGroup}>
              <h3>Delete Options</h3>
              <button 
                className={`${styles.configButton} ${styles.warningButton}`}
                onClick={handleDeleteLastRevision}
                disabled={processingAction}
              >
                Delete Last Revision
              </button>
              
              <button 
                className={`${styles.configButton} ${styles.dangerButton}`}
                onClick={handleDeleteCurve}
                disabled={processingAction}
              >
                Delete Curve
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal pour les fonctionnalités premium */}
      <PremiumHandler 
        isPremium={isPremium} 
        setIsPremium={setIsPremium} 
        showModal={showPremiumModal} 
        setShowModal={setShowPremiumModal} 
        errorMessage={quotaErrorMessage}
      />
    </div>
  );
};

export default CurvePage;
